import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { ClientResponse } from "@/Common/Api/ClientResponse";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { RadioButton } from "@/Common/Components/Inputs/RadioButton";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { DateTime } from "@/Common/Helpers/DateTime";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { MyleTableHelper } from "@/Common/Helpers/MyleTableHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { MyleDSQMedication } from "@/Common/Models/DSQ/MyleDSQMedication";
import { DsqMedicationDetail, DsqMedicationDispense, MyleDSQMedicationItem } from "@/Common/Models/DSQ/MyleDSQMedicationItem";
import { MedicalSummaryMedicationDetailsSortColumn } from "@/Common/Models/Patient/PatientEnums";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { DsqMedicationsTools } from "@/Patient/Components/Medication/DsqMedicationsTools";
import { MedicationModel } from "@/Patient/Models/Medication/MedicationModel";

import img_loading from "@/Globals/_images/loading.gif";

interface IDSQActiveMedGridComponent {
    onCloseCallback: () => void;
    patientContactId: string;
}

class DSQActiveMedGridComponentState {
    public From: Date;
    public To: Date;
    public SearchType: DsqActiveMedicationSearchType;
    public CanImport: boolean;

    public DSQMedicationList: Immutable.List<MyleDSQMedication>;
    public DSQMedicationListFiltered: Immutable.List<MyleDSQMedication>;
    public isLoading: boolean;
    public SelectAllMedicationisChecked: boolean;
    public EncounterId: string;
    public SearchText: string;

    public DsqMedicationDetail: DsqMedicationDetail;
    public DsqMedicationDispenses: DsqMedicationDispense[];
    public RightSidePanelMode: RightSidePanelMode;
    public RightSidePanelHide: boolean;

    public static getDefault(): DSQActiveMedGridComponentState {
        let s = new DSQActiveMedGridComponentState();
        s.From = null;
        s.To = null;
        s.SearchType = DsqActiveMedicationSearchType.Active;
        s.DSQMedicationList = [].toImmutable();
        s.DSQMedicationListFiltered = [].toImmutable();
        s.isLoading = false;
        s.SelectAllMedicationisChecked = false;
        s.RightSidePanelMode = RightSidePanelMode.None;
        s.RightSidePanelHide = false;
        return s;
    }
}

enum DsqActiveMedicationSearchType {
    Active,
    History,
    Pending
}

class DSQActiveMedGridComponent extends BaseReactComponent<IDSQActiveMedGridComponent, DSQActiveMedGridComponentState> {
    private tableColumns: Immutable.List<MyleTableColumn>;
    private tableRenderOptions: ITableRenderOptions;

    private saveOptionKey: string = "DSQActiveMedGridv2";
    private componentId: string = "DSQ_ActiveMedPopup";

    private readonly headHeight: number = 45;
    private readonly searchBarHeight: number = 82;
    private readonly footerHeight: number = 55;
    private readonly mainHeight: string = `calc(100% - ${this.headHeight}px - ${this.searchBarHeight}px - ${this.footerHeight}px`;

    public constructor(props: IDSQActiveMedGridComponent) {
        super(props);
        const initialState = DSQActiveMedGridComponentState.getDefault();
        initialState.EncounterId = patientStore.PatientUIModels[this.props.patientContactId].get("EditEncounterIdContext");
        this.state = initialState;

        this.loadTableOptions();
    }

    public componentDidUpdate(): void {
        this.initDraggableUi();
    }

    public componentDidMount(): void {
        overlayStore.showOverlay(this.componentId);
        this.initDraggableUi();
        this.searchDSQMedication();
    }

    public componentWillUnmount(): void {
        patientStore.EmitChangeToListeners(); // Renewals info might have been updated
        overlayStore.hideOverlay(this.componentId);
        super.componentWillUnmount();
    }

    public render(): JSX.Element {
        return (
            <div id={this.componentId} className="dsq-activemed-popup popup-attachment-main popup-with-grid">
                {/* Head */}
                <div className="dam-header p-header draggable" style={{ height: `${this.headHeight}px` }}>
                    <div className="flex-container">
                        <div><h2>{cacheStore.GetString("DsqMedicationLabel")}</h2></div>
                        <div><a className="close" onClick={() => this.props.onCloseCallback()}>x</a></div>
                    </div>
                </div>

                {/* Search */}
                <div style={{ height: `${this.searchBarHeight}px` }}>
                    {this.RenderSearchHeader()}
                </div>

                {/* Body */}
                <div className="dam-main" style={{ height: this.mainHeight }}>
                    <div className="flex-container" style={{ "justifyContent": "flex-start", height: "100%" }}>
                        {/* Left Side */}
                        <div>
                            <div className="fxheightpopup flex-container">
                                <MyleTable
                                    TableRenderOptions={this.tableRenderOptions}
                                    TableId="communications_message_table"
                                    Columns={this.tableColumns}
                                    DataSource={this.state.DSQMedicationListFiltered}
                                    RowClickFunction={item => this.OpenDSQDetails(item)}
                                    SaveOptionskeyTarget={this.saveOptionKey}
                                    onColumnPreferenceChange={() => this.loadTableOptions()}
                                />
                            </div>
                        </div>

                        {/* Separator */}
                        <div
                            className="flex-container aside-handle aside-handle-topborder aside-handle-verticallyCenteredElements"
                            onClick={() => this.mutateState(s => s.RightSidePanelHide = !this.state.RightSidePanelHide)}
                        >
                            <div className="slide-menu-icon blue"><span /><span /><span /></div>
                        </div>

                        {/* Right Side */}
                        {this.ShouldRightSidePanelDisplay() && (
                            <div style={{ overflowY: "auto", minWidth: this.GetPanelWidthStyle(DsqMedicationPanel.RightSide), maxWidth: this.GetPanelWidthStyle(DsqMedicationPanel.RightSide) }}>
                                {this.state.RightSidePanelMode == RightSidePanelMode.MedicationDetails &&
                                    this.RightSideMedicationDetailsRender()
                                }
                                {this.state.RightSidePanelMode == RightSidePanelMode.MedicationDispenses &&
                                    this.RightSideMedicationDispensesRender()
                                    // this.PrintObject(this.state.DsqMedicationDispenses)
                                }
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="dam-footer" style={{ height: `${this.footerHeight}px` }}>
                    <div className="flex-container" style={{ justifyContent: "space-between" }}>
                        <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                            {this.state.isLoading && (
                                <div style={{ marginRight: "5px" }}>
                                    <img src={img_loading} alt="loading" />
                                </div>
                            )}
                            {!this.state.EncounterId && (
                                <div className="pull-right red" style={{ marginTop: "5px" }}>
                                    <span>{cacheStore.GetString("ImportDisabledNoteContextWarning")}</span>
                                </div>
                            )}
                        </div>
                        <div>
                            <div className="flex-container" style={{ "justifyContent": "flex-end" }}>
                                <div>
                                    <button
                                        className="btn btn-default"
                                        onClick={() => this.props.onCloseCallback()}
                                    >
                                        {cacheStore.GetString("CloseLabel")}
                                    </button>
                                </div>
                                <div style={{ "marginLeft": "5px" }}>
                                    <button
                                        className="btn btn-primary"
                                        disabled={!this.state.EncounterId || this.state.isLoading || this.state.CanImport == false}
                                        onClick={() => this.ImportSelectedMeds()}
                                    >
                                        {cacheStore.GetString("ImportLabel")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    public RenderSearchHeader(): JSX.Element {
        return (
            <div>
                <div>
                    <div className="flex-container" style={{ justifyContent: "space-between" }}>
                        <div style={{ width: "340px", marginTop: "3px" }}>
                            <RadioButton
                                items={[
                                    new KeyValuePair(cacheStore.GetString("ActiveOnlyLaval"), DsqActiveMedicationSearchType.Active),
                                    new KeyValuePair(cacheStore.GetString("HistoryLabel"), DsqActiveMedicationSearchType.History),
                                    new KeyValuePair(cacheStore.GetString("PendingLabel"), DsqActiveMedicationSearchType.Pending)
                                ].toImmutable()}
                                radioInputStyle={{ marginLeft: "-16px" }}
                                radioItemStyle={{ whiteSpace: "nowrap", marginTop: "0px", marginBottom: "0px" }}
                                radioInputGroupId={`${this.componentId}_SearchType`}
                                onChange={item => this.mutateState(s => s.SearchType = item.Value)}
                                selectedItem={this.state.SearchType}
                            />
                        </div>
                    </div>
                    <div className="flex-container" style={{ justifyContent: "space-between", marginTop: "3px" }}>
                        <div>
                            <div className="flex-container" style={{ marginTop: "10px", marginLeft: "23px" }}>
                                <div style={{ width: "200px" }}>
                                    <DateSelector
                                        id="dateDsq1"
                                        date={this.state.From}
                                        onChangeCallback={date => this.mutateState(s => s.From = date)}
                                        dateFormat="yyyy-MM-dd"
                                        disabled={false}
                                        placeholder={cacheStore.GetString("FromLabel")}
                                    />
                                </div>
                                <div style={{ width: "200px", marginLeft: "15px" }}>
                                    <DateSelector
                                        id="dateDsq2"
                                        date={this.state.To}
                                        onChangeCallback={_date => this.mutateState(s => s.To = _date)}
                                        dateFormat="yyyy-MM-dd"
                                        disabled={false}
                                        placeholder={cacheStore.GetString("ToLabel")}
                                    />
                                </div>
                                <div className="push-button-left">
                                    <button
                                        className="btn btn-primary"
                                        style={{ height: "38px" }}
                                        disabled={this.state.isLoading}
                                        onClick={() => this.searchDSQMedication()}
                                    >
                                        {cacheStore.GetString("SearchLabel")}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div style={{ maxWidth: "360px", flexGrow: 1, marginLeft: "15px", marginRight: "15px", marginTop: "10px" }}>
                            <TextboxControl
                                debounceMs={400}
                                value={this.state.SearchText}
                                style={{ marginRight: "10px" }}
                                className="form-control"
                                placeholder={cacheStore.GetString("SearchLabel")}
                                onChange={text => this.mutateState(s => s.SearchText = text)}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    public RightSideMedicationDetailsRender(): JSX.Element {
        let medecine = this.state.DsqMedicationDetail.Medecine;
        let medecineDosage = this.state.DsqMedicationDetail.Dosages?.firstOrDefault();

        let recentDispense = this.state.DsqMedicationDetail.LastDispense;
        let ceaseAction = this.state.DsqMedicationDetail.CeaseHistory?.sortByPropertyDesc(p => moment.utc(p.Date))?.firstOrDefault();

        return (
            <div>
                <div className="dsq-med-details-section">
                    <div>{this.getString("PrescriptionDSQLabel")?.toUpperCase()}</div>
                    <div>
                        <div className="dsq-med-details-normal-subsection">
                            <div>
                                <div>{this.getString("MedicationLabel")}</div>
                                <div>{medecine.MedicationName}</div>
                            </div>
                            <div>
                                <div>{this.getString("MedicationCommercialNameLabel")}</div>
                                <div>{medecine.CommercialName}</div>
                            </div>
                            <div>
                                <div>{this.getString("DINLabel")}</div>
                                <div>{medecine.Din}</div>
                            </div>
                            <div>
                                <div>{this.getString("PrescriptionStatusLabel")}</div>
                                <div>{this.state.DsqMedicationDetail.PrescriptionStatus}</div>
                            </div>
                            <div>
                                <div>{this.getString("lblMedicationForme")}</div>
                                <div>{medecine.Form}</div>
                            </div>
                            <div>
                                <div>{this.getString("RouteLabel")}</div>
                                <div>{medecineDosage?.Route}</div>
                            </div>
                            <div>
                                <div>{this.getString("lblDSQPosologyText")}</div>
                                <div>
                                    <div>{medecineDosage?.Instruction}</div>
                                    <div>{this.state.DsqMedicationDetail.CodeException}</div>
                                </div>
                            </div>
                        </div>

                        {medecine?.MedicationIngredients &&
                            /* Splitter */
                            <div className="dsq-med-details-subsection-seperator" />
                        }
                        {medecine?.MedicationIngredients && (
                            <div className="dsq-med-details-normal-subsection">
                                <div>
                                    <div style={{ textDecorationLine: "underline" }}>
                                        {this.getString("MagistralLabel")}
                                    </div>
                                    <div />
                                </div>
                                <div>
                                    <div>{this.getString("lblMedicationIngredients")}</div>
                                    <div>
                                        {medecine.MedicationIngredients &&
                                            medecine.MedicationIngredients
                                                .map(ingredient => <div key={ingredient}>{ingredient}</div>)
                                        }
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Splitter */}
                        <div className="dsq-med-details-subsection-seperator" />

                        <div className="dsq-med-details-normal-subsection">
                            <div>
                                <div>{this.getString("PrescriptionDateLabel")}</div>
                                <div>{this.state.DsqMedicationDetail.PrescriptionOrderDate}</div>
                            </div>
                            <div>
                                <div>{this.getString("PrescribedByLabel")}</div>
                                <div>{this.state.DsqMedicationDetail.Author?.Name}</div>
                            </div>
                            <div>
                                <div>{this.getString("PermitNumberLabel")}</div>
                                <div>{this.state.DsqMedicationDetail.Author?.Licence}</div>
                            </div>
                            <div>
                                <div>{this.getString("PrescriptionAtLocationLabel")}</div>
                                <div>{this.state.DsqMedicationDetail.PrescribedAt?.Name}</div>
                            </div>
                            <div>
                                <div>{this.getString("HealthCareContextLabel")}</div>
                                <div>{this.state.DsqMedicationDetail.PrescribedAt?.LocationCode}</div>
                            </div>
                            <div>
                                <div>{this.getString("lblTotalQuantityPrescribed")}</div>
                                <div>{this.state.DsqMedicationDetail.DispenseRequest?.TotalPrescribedQuantity}</div>
                            </div>
                            <div>
                                <div>{this.getString("TotalDurationLabel")}</div>
                                <div>{this.formatDsqDuration(this.state.DsqMedicationDetail.DispenseRequest?.TotalDaysSupply)}</div>
                            </div>
                        </div>

                        {/* Splitter */}
                        <div className="dsq-med-details-subsection-seperator" />

                        <div className="dsq-med-details-normal-subsection">
                            <div>
                                <div>{this.getString("lblPharmacie")}</div>
                                <div>
                                    <div>{this.state.DsqMedicationDetail.DispenseRequest?.Pharmacy?.Name}</div>
                                    <div>{this.state.DsqMedicationDetail.DispenseRequest?.Pharmacy?.Street} {this.state.DsqMedicationDetail.DispenseRequest?.Pharmacy?.State}</div>
                                    <div>{this.state.DsqMedicationDetail.DispenseRequest?.Pharmacy?.Zip}</div>
                                    {this.state.DsqMedicationDetail.DispenseRequest?.Pharmacy?.Phone &&
                                        <div>T: {ValidationHelper.formatPhoneNumberCanadaUS(this.state.DsqMedicationDetail.DispenseRequest?.Pharmacy?.Phone)}</div>
                                    }
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

                {recentDispense && (
                    <div className="dsq-med-details-section">
                        <div>{this.getString("RecentDispensationLabel")?.toUpperCase()}</div>
                        <div>
                            <div className="dsq-med-details-normal-subsection">
                                <div>
                                    <div>{this.getString("EventLabel")}</div>
                                    <div>{recentDispense.EventDescription}</div>
                                </div>
                                <div>
                                    <div>{this.getString("MedicationLabel")}</div>
                                    <div>{recentDispense.Medicine?.MedicationName}</div>
                                </div>
                                <div>
                                    <div>{this.getString("MedicationCommercialNameLabel")}</div>
                                    <div>{recentDispense.Medicine?.CommercialName}</div>
                                </div>
                                <div>
                                    <div>{this.getString("DINLabel")}</div>
                                    <div>{recentDispense.Medicine?.Din}</div>
                                </div>
                                <div>
                                    <div>{this.getString("lblDSQPosologyText")}</div>
                                    <div>
                                        <div>{recentDispense.Dosages?.firstOrDefault()?.Instruction}</div>
                                        <div>{this.state.DsqMedicationDetail.CodeException}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Recent Dispensation Magistrale */}
                            {recentDispense.Medicine?.MedicationIngredients &&
                                /* Splitter */
                                <div className="dsq-med-details-subsection-seperator" />
                            }
                            {recentDispense.Medicine?.MedicationIngredients && (
                                <div className="dsq-med-details-normal-subsection">
                                    <div>
                                        <div style={{ textDecorationLine: "underline" }}>
                                            {this.getString("MagistralLabel")}
                                        </div>
                                        <div />
                                    </div>
                                    <div>
                                        <div>{this.getString("lblMedicationIngredients")}</div>
                                        <div>
                                            {recentDispense.Medicine?.MedicationIngredients
                                                .map(ingredient => <div key={ingredient}>{ingredient}</div>)
                                            }
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Splitter */}
                            <div className="dsq-med-details-subsection-seperator" />

                            <div className="dsq-med-details-normal-subsection">
                                <div>
                                    <div>{this.getString("DispensationDateLabel")}</div>
                                    <div>{recentDispense.PickupDate}</div>
                                </div>
                                <div>
                                    <div>{this.getString("ServedByLabel")}</div>
                                    <div>{recentDispense.Author?.Name}</div>
                                </div>
                                <div>
                                    <div>{this.getString("PermitNumberLabel")}</div>
                                    <div>{recentDispense.Author?.Licence}</div>
                                </div>
                                <div>
                                    <div>{this.getString("QuantityLabel")}</div>
                                    <div>{recentDispense.Quantity}</div>
                                </div>
                                <div>
                                    <div>{this.getString("DurationLabel")}</div>
                                    <div>{this.formatDsqDuration(recentDispense.DaysSupply)}</div>
                                </div>
                            </div>

                            {/* Splitter */}
                            <div className="dsq-med-details-subsection-seperator" />

                            <div className="dsq-med-details-normal-subsection">
                                <div>
                                    <div>{this.getString("lblPharmacie")}</div>
                                    <div>
                                        <div>{recentDispense.Pharmacy?.Name}</div>
                                        <div>{recentDispense.Pharmacy?.Street} {this.state.DsqMedicationDetail.DispenseRequest?.Pharmacy?.State}</div>
                                        <div>{recentDispense.Pharmacy?.Zip}</div>
                                        {recentDispense.Pharmacy?.Phone &&
                                            <div>T: {ValidationHelper.formatPhoneNumberCanadaUS(recentDispense.Pharmacy?.Phone)}</div>
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {ceaseAction && (
                    <div className="dsq-med-details-section">
                        <div>{this.getString("TerminationMedicationLabel")?.toUpperCase()}</div>
                        <div>
                            <div className="dsq-med-details-normal-subsection">
                                <div>
                                    <div>{this.getString("TerminationMedicationDateLabel")}</div>
                                    <div>{ceaseAction.Date && moment(ceaseAction.Date).format("YYYY-MM-DD")}</div>
                                </div>
                                <div>
                                    <div>{this.getString("ReasonLabel")}</div>
                                    <div>{ceaseAction.Reason}</div>
                                </div>
                                <div>
                                    <div>{this.getString("CeasedByLabel")}</div>
                                    <div>{`${ceaseAction.Provider}`}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {this.state.DsqMedicationDetail.Refusals && (
                    <div className="dsq-med-details-section">
                        <div>{this.getString("RefusalLabel")?.toUpperCase()}</div>
                        <div>
                            {this.state.DsqMedicationDetail.Refusals
                                .map((refusal, key) => (
                                    <div className="dsq-med-details-normal-subsection" key={`detailNote-${key}`}>
                                        <div>
                                            <div>{this.getString("LastRefusalDate")}</div>
                                            <div>{refusal.Date && moment(refusal.Date).format("YYYY-MM-DD")}</div>
                                        </div>
                                        <div>
                                            <div>{this.getString("RefusalByLabel")}</div>
                                            <div>{`[${refusal.ProviderType}] ${refusal.Provider}`}</div>
                                        </div>
                                        <div>
                                            <div>{this.getString("lblPharmacie")}</div>
                                            <div>{refusal.Parmacy?.Name}</div>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </div>
                )}

                {this.state.DsqMedicationDetail.Notes && (
                    <div className="dsq-med-details-section">
                        <div>{this.getString("NotesLabel")?.toUpperCase()}</div>
                        <div>
                            {this.state.DsqMedicationDetail.Notes.map((note, key) => (
                                <div className="dsq-med-details-normal-subsection" key={`detailNote-${key}`}>
                                    <div>
                                        <div>{this.getString("lblDSQPresctionNote")}</div>
                                        <div>{note.Text}</div>
                                    </div>
                                    <div>
                                        <div>{this.getString("DateLabel")}</div>
                                        <div>{note.Date && moment(note.Date).format("YYYY-MM-DD")}</div>
                                    </div>
                                    <div>
                                        <div>{this.getString("lblDSQPresctionNoteBy")}</div>
                                        <div>{note.Author}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    public RightSideMedicationDispensesRender(): JSX.Element {
        return (
            <div>
                <div className="dsq-med-details-section">
                    <div>{this.getString("DispensationsLabel")?.toUpperCase()}</div>

                    {this.state.DsqMedicationDispenses?.map(medication => {
                        let dateStyle: React.CSSProperties = medication.IsCancelled
                            ? { textDecoration: "line-through" }
                            : {};

                        return (
                            <div key={medication.Number}>
                                <div className="dsq-med-details-normal-subsection">
                                    <div>
                                        <div>{this.getString("StatusLabel")}</div>
                                        <div style={{ textDecorationLine: "underline" }}>{medication.Status}</div>
                                    </div>
                                    <div>
                                        <div>{this.getString("DispensationDateLabel")}</div>
                                        <div style={dateStyle}>{medication.PickupDate}</div>
                                    </div>
                                    <div>
                                        <div>{this.getString("ServedByLabel")}</div>
                                        <div>{medication.Author?.Name}</div>
                                    </div>
                                    <div>
                                        <div>{this.getString("MedicationCommercialNameLabel")}</div>
                                        <div>{medication.Medicine?.CommercialName}</div>
                                    </div>
                                    <div>
                                        <div>{this.getString("PermitNumberLabel")}</div>
                                        <div>{medication.Author?.Licence}</div>
                                    </div>
                                    <div>
                                        <div>{this.getString("QuantityLabel")}</div>
                                        <div>{medication.Quantity}</div>
                                    </div>
                                    <div>
                                        <div>{this.getString("DurationLabel")}</div>
                                        <div>{this.formatDsqDuration(medication.DaysSupply)}</div>
                                    </div>
                                    <div>
                                        <div>{this.getString("lblDSQPosologyText")}</div>
                                        <div>{medication?.Instruction}</div>
                                    </div>
                                    <div>
                                        <div>{this.getString("lblPharmacie")}</div>
                                        <div>{medication?.Pharmacy?.Name}</div>
                                    </div>
                                </div>

                                {/* Splitter */}
                                <div className="dsq-med-details-subsection-seperator" />
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    private GetTableColumns(columnsOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<MyleDSQMedication>(columnsOptions, column => {
            switch (column.FieldName) {
                case "Selection":
                    return {
                        renderCell: item => {
                            if (item.IsAlreadyInMyle || !item.Model.IsActive)
                                return (<div />);

                            return (
                                <div onClick={() => this.SelectMedicationDSQ(item)}>
                                    <input type="checkbox" id={item.Model.DSQMedicationID} checked={item.IsSelected} />
                                </div>
                            );
                        },
                        renderHeader: () => {
                            if (this.state?.SearchType == DsqActiveMedicationSearchType.Pending)
                                return (<div />);

                            return (
                                <div className="checkbox-col">
                                    <input
                                        type="checkbox"
                                        className="header-input"
                                        onChange={() => this.ToggleSelectAllMedicationDSQ()}
                                        checked={this.state.SelectAllMedicationisChecked}
                                    />
                                </div>
                            );
                        }
                    };

                case "Medication":
                    column.columnClass = "wsnormalTableDiv";
                    return {
                        renderCell: item => (
                            <div>
                                <div title={item.Model.NameStrength}><span>{item.Model.NameStrength}</span></div>
                            </div>
                        )
                    };

                case "Beginning":
                    return {
                        renderCell: item => {
                            let DateDisplay = "";
                            if (item.Model.StartDate != null)
                                DateDisplay = moment(item.Model.StartDate).format("YYYY-MM-DD");
                            return <div title={DateDisplay}><span>{DateDisplay}</span></div>;
                        }
                    };

                case "PrescribedBy":
                    return {
                        renderCell: item => (
                            <div>
                                <div><span title={item.Model.PrescribedBy}>{item.Model.PrescribedBy}</span></div>
                            </div>
                        )
                    };

                case "Dosage":
                    column.columnClass = "wsnormalTableDiv";
                    return {
                        renderCell: item => (
                            <div><span>{item.Model.Dosage}</span></div>
                        )
                    };

                case "Pharmacy":
                    return {
                        renderCell: item => {
                            let name = item.Model.Pharmacy?.PharmaName;
                            let address = `${item.Model.Pharmacy?.PharmaAdress ?? ""} ${item.Model.Pharmacy?.PharmaCity ?? ""}`;
                            let phone = ValidationHelper.formatPhoneNumberCanadaUS(item.Model.Pharmacy?.PharmaPhone);
                            return (
                                <div>
                                    <div title={name}><span>{name}</span></div>
                                    <div title={address}><span>{address}</span></div>
                                    <div title={phone}><span>{phone}</span></div>
                                </div>
                            );
                        }
                    };

                case "Quantity":
                    return {
                        renderCell: item => (
                            <div><span>{item.Model.TotalQuantity}</span></div>
                        )
                    };

                case "Renewal":
                    return {
                        renderCell: item => {
                            let daysSinceAction = "";
                            let daysSinceLabel = "";
                            if (!IsNullOrWhiteSpace(item.Model.LastPickupDate)) {
                                daysSinceAction = DateTime.nowMoment().diff(moment(item.Model.LastPickupDate), "days").toString();
                                daysSinceLabel = this.getString("DaysSinceLastDispenseLabel");
                            }
                            else {
                                daysSinceAction = DateTime.nowMoment().diff(moment(item.Model.StartDate), "days").toString();
                                daysSinceLabel = this.getString("DaysSincePrescribedLabel");
                            }

                            let remainingStyle: React.CSSProperties = item.Model.Status?.startsWith("Active")
                                ? {}
                                : { textDecoration: "line-through" };

                            let daysSinceDispensationFormatted = `${daysSinceAction} ${daysSinceLabel}`;
                            if (item.Model.IsPendingMedication)
                                return <div><div title={daysSinceDispensationFormatted}><span>{daysSinceDispensationFormatted}</span></div></div>;

                            return (
                                <div>
                                    <div>
                                        <span>{`${cacheStore.GetString("TotalLabel")}: ${item.Model.NumberOfRefills ?? ""}`}</span>
                                    </div>
                                    <div style={remainingStyle}>
                                        <span>{`${item.Model.RefillsRemaining ?? ""} ${cacheStore.GetString("RemainingLabel")}`}</span>
                                    </div>
                                    <div title={daysSinceDispensationFormatted}>
                                        <span>{daysSinceDispensationFormatted}</span>
                                    </div>
                                    {!IsNullOrWhiteSpace(item.Model.LastPickupDate) && (
                                        <div>
                                            <a onClick={() => this.OpenDelivranceDetails(item)}>
                                                {cacheStore.GetString("DispensationHistoryLabel")}
                                            </a>
                                        </div>
                                    )}
                                </div>
                            );
                        }
                    };

                case "Status":
                    return {
                        renderCell: item => (
                            <div><span title={item.Model.Status}>{item.Model.Status}</span></div>
                        )
                    };

                case "Actions":
                    return {
                        renderCell: item => {
                            if (item.Model.IsPendingMedication && !IsNullOrWhiteSpace(item.Model.DSQEventId))
                                return (
                                    <div>
                                        <a onClick={() => this.CancelPendingPrescription(item.Model.DSQEventId, item.Model.DSQOIDVersion)}>
                                            {this.getString("CancelLabel")}
                                        </a>
                                    </div>
                                );

                            return (<div><span /></div>);
                        }
                    };
            }
        });
    }

    private initDraggableUi(): void {
        let ref = $("#" + this.componentId);
        if (ref.hasClass("ui-draggable"))
            ref.draggable("destroy");

        ref.draggable({
            cancel: "input, button, label",
            handle: "." + "p-header"
        });
    }

    public mutateState(mutation: (state: DSQActiveMedGridComponentState) => void, then: () => void = null): void {
        super.mutateState(
            s => {
                mutation(s);
                s.CanImport = s.DSQMedicationList?.some(x => x.IsSelected == true && x.IsAlreadyInMyle == false && x.Model.IsActive == true);

                s.DSQMedicationListFiltered = IsNullOrWhiteSpace(s.SearchText)
                    ? s.DSQMedicationList
                    : s.DSQMedicationList.toArray().filterSearch(s.SearchText, p => `${p.Model.NameStrength} ${p.Model.Dosage}`).toImmutable();
            },
            then
        );
    }

    public loadTableOptions(): void {
        let cols = [
            MyleTableColumnOptions.DefaultFixed(" ", "Selection", 30, false),
            MyleTableColumnOptions.Default(cacheStore.GetString("MedicationLabel"), "Medication", 17, true, MedicalSummaryMedicationDetailsSortColumn.Medication),
            MyleTableColumnOptions.Default(cacheStore.GetString("BeginningLabel"), "Beginning", 8, true, MedicalSummaryMedicationDetailsSortColumn.Beginning),
            MyleTableColumnOptions.Default(cacheStore.GetString("PrescribedByLabel"), "PrescribedBy", 11, true, MedicalSummaryMedicationDetailsSortColumn.Ended),
            MyleTableColumnOptions.Default(cacheStore.GetString("DosageLabel"), "Dosage", 18, true, MedicalSummaryMedicationDetailsSortColumn.Dosage),
            MyleTableColumnOptions.Default(cacheStore.GetString("PharmacyNameLabel"), "Pharmacy", 17, true, MedicalSummaryMedicationDetailsSortColumn.Pharmacy),
            MyleTableColumnOptions.Default(cacheStore.GetString("QuantityLabel"), "Quantity", 4, true, MedicalSummaryMedicationDetailsSortColumn.Quantity),
            MyleTableColumnOptions.Default(cacheStore.GetString("RenewalsLabel"), "Renewal", 13, false, MedicalSummaryMedicationDetailsSortColumn.Renewals),
            MyleTableColumnOptions.Default(cacheStore.GetString("StatusLabel"), "Status", 5, true, MedicalSummaryMedicationDetailsSortColumn.Status),
            MyleTableColumnOptions.Default(cacheStore.GetString("ActionsLabel"), "Actions", 5, false, MedicalSummaryMedicationDetailsSortColumn.Actions)
        ];

        let columnsOptions = sessionStore.helper.loadTableColumnOptionsFromSettings(this.saveOptionKey, cols);
        this.tableColumns = this.GetTableColumns(columnsOptions);
        this.tableRenderOptions = MyleTableHelper
            .getDefaultRenderOptions(columnsOptions.toArray())
            .set("LastColumnNoDrag", false)
            .set("HeightOffset", 65)
            .set("MinColWidth", 31);
    }

    public formatDsqDuration(duration: string): string {
        let splittedDuration = duration?.split(" jour(s)");
        if (splittedDuration?.length > 1) {
            let daysDuration = splittedDuration[0];
            return MyleHelper.GetFormattedDuration(daysDuration);
        }
        return duration;
    }

    public ShouldRightSidePanelDisplay(): boolean {
        return this.state.RightSidePanelMode != RightSidePanelMode.None
            && !this.state.RightSidePanelHide
            && (this.state.DsqMedicationDetail != null || this.state.DsqMedicationDispenses != null);
    }

    public GetPanelWidthStyle(panel: DsqMedicationPanel): string {
        if (panel == DsqMedicationPanel.RightSide)
            return this.ShouldRightSidePanelDisplay() ? "450px" : "0px";
    }

    private SelectMedicationDSQ(item: MyleDSQMedication): void {
        this.mutateState(s => {
            s.DSQMedicationList = this.state.DSQMedicationList
                .mutate(
                    x => x.IsSelected = !x.IsSelected,
                    x => x.Model.DSQMedicationID == item.Model.DSQMedicationID
                );
        });
    }

    private ToggleSelectAllMedicationDSQ(): void {
        this.mutateState(s => {
            s.SelectAllMedicationisChecked = !this.state.SelectAllMedicationisChecked;
            s.DSQMedicationList = this.state.DSQMedicationList
                .mutate(x => x.IsSelected = s.SelectAllMedicationisChecked && !x.IsAlreadyInMyle && x.Model.IsActive);
        });
    }

    private ImportSelectedMeds(): void {
        let MedicationsToSave = this.state.DSQMedicationListFiltered
            .filter(x => x.IsSelected == true && x.IsAlreadyInMyle == false && x.Model.IsActive == true)
            .toArray()
            .map(p => MedicationModel.FromMyleDSQMedicationItem(p.Model, true));

        if (MedicationsToSave.length > 0 && this.state.EncounterId) {
            patientStore.ActionsMedicationTab.UpsertMedication(MedicationsToSave, this.props.patientContactId, this.state.EncounterId);
            this.props.onCloseCallback();
        }
    }

    public searchDSQMedication(): void {
        this.mutateState(
            s => s.isLoading = true,
            () => {
                // Search Pending Medication
                if (this.state.SearchType == DsqActiveMedicationSearchType.Pending) {
                    Client.PostDSQ(
                        ServerOperations.Server.DSQ_GetPendingMedications,
                        {
                            PatientContactId: this.props.patientContactId,
                            StartDate: this.state.From,
                            EndDate: this.state.To
                        },
                        response =>
                            this.handleMedicationSearchResponse(
                                new ClientResponse<DsqMedicationSearchResponse>(
                                    response as DsqMedicationSearchResponse,
                                    response.Errors
                                )
                            )

                    );
                    return;
                }

                // Search Summary
                Client.PostDSQ(
                    ServerOperations.Server.DSQ_MedicationSummary,
                    {
                        PatientContactId: this.props.patientContactId,
                        StartDate: this.state.From,
                        EndDate: this.state.To,
                        ActiveMedicationOnly: this.state.SearchType == DsqActiveMedicationSearchType.Active
                    },
                    response =>
                        this.handleMedicationSearchResponse(
                            new ClientResponse<DsqMedicationSearchResponse>(
                                response as DsqMedicationSearchResponse,
                                response.Errors
                            )
                        )
                );
            }
        );
    }

    public handleMedicationSearchResponse(clientResponse: ClientResponse<DsqMedicationSearchResponse>): void {
        this.mutateState(s => {
            s.isLoading = false;
            s.DSQMedicationList = DsqMedicationsTools
                .InitDSQMedicationitems(
                    clientResponse.data.myleDSQMedications,
                    this.props.patientContactId,
                    this.state.SearchType == DsqActiveMedicationSearchType.Active
                )
                .toImmutable();
            s.SelectAllMedicationisChecked = s.DSQMedicationList.toArray().all(p => p.IsSelected);
            s.RightSidePanelHide = true;
        });
    }

    public CancelPendingPrescription(DsqEventId: string, PrescriptionOriginalOID: string): void {
        this.mutateState(
            s => s.isLoading = true,
            () => {
                Client.PostDSQ(
                    ServerOperations.Server.DSQ_CancelPrescribe,
                    {
                        PatientContactId: this.props.patientContactId,
                        DsqEventId: DsqEventId,
                        PrescriptionOriginalOID: PrescriptionOriginalOID
                    },
                    response => {
                        this.mutateState(
                            s => s.isLoading = false,
                            () => {
                                if (response.Errors.length == 0)
                                    this.searchDSQMedication();
                            }
                        );
                    }
                );
            }
        );
    }

    public OpenDSQDetails(item: MyleDSQMedication): void {
        this.mutateState(
            s => s.isLoading = true,
            () => {
                if (item.Model.IsPendingMedication) {
                    // Client.PostDSQ(
                    //     Server.DSQ_GetPendingMedicationDetails,
                    //     {
                    //         PatientContactId: this.props.patientContactId,
                    //         IsDevice: item.Model.IsDevice,
                    //         PrescriptionID: item.Model.DSQMedicationID,
                    //         PrescriptionVersion: item.Model.DSQPrescriptionVersion,
                    //     },
                    //     response => this.mutateState(s => {
                    //         //s.DsqPendingMedicationDetail = response.DsqPendingMedicationDetail;
                    //         s.RightSidePanelMode = RightSidePanelMode.MedicationDetails;
                    //         s.RightSidePanelHide = false;
                    //         s.isLoading = false;
                    //     })
                    // );
                    return;
                }

                Client.PostDSQ(
                    ServerOperations.Server.DSQ_GetMedicationSummaryDetails,
                    {
                        PatientContactId: this.props.patientContactId,
                        IsOther: item.Model.IsOtherMedicationDSQ,
                        PrescriptionID: item.Model.DSQMedicationID,
                        PrescriptionVersion: item.Model.DSQPrescriptionVersion
                    },
                    response => this.mutateState(s => {
                        s.DsqMedicationDetail = response.MedicationDetail;
                        s.RightSidePanelMode = RightSidePanelMode.MedicationDetails;
                        s.RightSidePanelHide = false;
                        s.isLoading = false;
                    })
                );
            }
        );
    }

    public OpenDelivranceDetails(item: MyleDSQMedication): void {
        this.mutateState(
            s => s.isLoading = true,
            () => {
                Client.PostDSQ(
                    ServerOperations.Server.DSQ_GetDelivranceList,
                    {
                        PatientContactId: this.props.patientContactId,
                        PrescriptionNumber: item.Model.DSQMedicationID,
                        PrescriptionVersion: item.Model.DSQPrescriptionVersion
                    },
                    response => this.mutateState(s => {
                        s.DsqMedicationDispenses = response.Dispenses;
                        s.RightSidePanelMode = RightSidePanelMode.MedicationDispenses;
                        s.RightSidePanelHide = false;
                        s.isLoading = false;
                    })
                );
            }
        );
    }
}

class DsqMedicationSearchResponse {
    public myleDSQMedications: MyleDSQMedicationItem[];
}

enum DsqMedicationPanel {
    LeftSide,
    RightSide
}

enum RightSidePanelMode {
    None,
    MedicationDetails,
    MedicationDispenses
}

export { DSQActiveMedGridComponent, DsqActiveMedicationSearchType };
